/**
 * Team leave calendar — a month grid, coloured by who is away.
 *
 * The purpose is coverage, not decoration: a manager approving a request needs to see at
 * a glance who else is already off that week. Approved and pending are visually distinct
 * (pending is outlined, not filled) because approving a second person onto a day that
 * already has a pending request is exactly the mistake this screen prevents.
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useHolidays, useTeamCalendar, type CalendarDay } from '@frontend/api/hooks/leave';
import { cn } from '@frontend/lib/cn';
import { humanise, parseISODate } from '@frontend/lib/format';
import { IconButton } from '@frontend/components/ui/Button';
import { PageHeader } from '@frontend/components/ui/Page';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';

function monthBounds(year: number, month: number): { from: string; to: string } {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const iso = (date: Date): string =>
    `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
  return { from: iso(first), to: iso(last) };
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function TeamCalendarPage(): JSX.Element {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const { from, to } = monthBounds(year, month);

  const calendar = useTeamCalendar(from, to);
  const holidays = useHolidays(year);

  const byDate = useMemo(() => {
    const source = calendar.data?.days ?? calendar.data?.entries ?? [];
    const map = new Map<string, CalendarDay[]>();
    for (const entry of source) {
      map.set(entry.date, [...(map.get(entry.date) ?? []), entry]);
    }
    return map;
  }, [calendar.data]);

  const holidayByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const holiday of holidays.data?.holidays ?? []) map.set(holiday.date, holiday.name);
    return map;
  }, [holidays.data]);

  // Build the grid, Monday-first, padded to whole weeks.
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leadingBlanks = (first.getDay() + 6) % 7; // Sunday=0 → Monday-first
    const output: (string | null)[] = Array.from({ length: leadingBlanks }, () => null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      output.push(`${year}-${`${month + 1}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`);
    }
    while (output.length % 7 !== 0) output.push(null);
    return output;
  }, [year, month]);

  function shift(delta: number): void {
    const next = new Date(year, month + delta, 1);
    setYear(next.getFullYear());
    setMonth(next.getMonth());
  }

  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(year, month, 1),
  );

  return (
    <div>
      <PageHeader
        title="Team calendar"
        description="Who is away, and when. Pending requests are outlined; approved leave is filled."
        actions={
          <div className="flex items-center gap-1">
            <IconButton size="sm" label="Previous month" icon={<ChevronLeft className="h-4 w-4" />} onClick={() => shift(-1)} />
            <span className="min-w-[140px] text-center text-row font-medium text-content">{monthLabel}</span>
            <IconButton size="sm" label="Next month" icon={<ChevronRight className="h-4 w-4" />} onClick={() => shift(1)} />
          </div>
        }
      />

      {calendar.isLoading ? (
        <LoadingRows rows={6} columns={7} />
      ) : calendar.isError ? (
        <ErrorState
          error={calendar.error}
          onRetry={() => {
            void calendar.refetch();
          }}
        />
      ) : byDate.size === 0 && holidayByDate.size === 0 ? (
        <div className="surface-card">
          <EmptyState
            title={`Nobody is booked off in ${monthLabel}`}
            description="Approved and pending leave for everyone you can see will appear on this calendar. Use the arrows to look at another month."
          />
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <div className="grid grid-cols-7 border-b border-line bg-surface-subtle">
            {WEEKDAYS.map((day) => (
              <div
                key={day}
                className="px-2 py-1.5 text-body font-semibold uppercase tracking-[0.04em] text-content-secondary"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((date, index) => {
              if (date === null) {
                return <div key={`blank-${index}`} className="min-h-[92px] border-b border-r border-line-subtle bg-surface-subtle/40" />;
              }
              const entries = byDate.get(date) ?? [];
              const holiday = holidayByDate.get(date);
              const weekday = (parseISODate(date).getDay() + 6) % 7;
              const isWeekend = weekday >= 5;

              return (
                <div
                  key={date}
                  className={cn(
                    'min-h-[92px] border-b border-r border-line-subtle p-1.5',
                    isWeekend && 'bg-surface-subtle/60',
                  )}
                  data-testid={`calendar-day-${date}`}
                >
                  <div className="mb-1 flex items-baseline justify-between">
                    <span className="text-body tabular text-content-secondary">{Number(date.slice(8, 10))}</span>
                    {entries.length > 2 && (
                      <span className="text-body text-content-tertiary">{entries.length} away</span>
                    )}
                  </div>
                  {holiday !== undefined && (
                    <p className="mb-1 truncate rounded-sm bg-info-bg px-1 py-0.5 text-body text-content" title={holiday}>
                      {holiday}
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {entries.slice(0, 3).map((entry, entryIndex) => {
                      const isPending = entry.status.startsWith('PENDING') || entry.status === 'SUBMITTED';
                      return (
                        <li
                          key={`${entry.employeeId}-${entryIndex}`}
                          title={`${entry.employeeName ?? entry.employeeId} · ${humanise(entry.leaveType)} · ${humanise(entry.status)}`}
                          className={cn(
                            'truncate rounded-sm px-1 py-0.5 text-body',
                            isPending
                              ? 'border border-dashed border-status-pending-border text-status-pending-fg'
                              : 'bg-status-approved-bg text-status-approved-fg',
                          )}
                        >
                          {entry.employeeName ?? entry.employeeId}
                        </li>
                      );
                    })}
                    {entries.length > 3 && (
                      <li className="px-1 text-body text-content-tertiary">+{entries.length - 3} more</li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
